"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentUser } from "@/hooks/use-current-user";
import type { CurrentUser } from "@/lib/feed/types";
import {
  markIntroductionServicesViewed,
  skipIntroductionChecklist,
} from "@/lib/onboarding/api";
import { queryKeys } from "@/lib/query-keys";

const INTRODUCTION_LIFETIME_MS = 5 * 24 * 60 * 60 * 1000;

export function IntroductionProgressTracker() {
  const pathname = usePathname();
  const currentUserQuery = useCurrentUser();
  const queryClient = useQueryClient();
  const user = currentUserQuery.data;

  const {
    mutate: markServicesViewed,
    isPending: isMarkingServices,
  } = useMutation({
    mutationFn: markIntroductionServicesViewed,
    onSuccess: () => {
      queryClient.setQueryData<CurrentUser | null>(
        queryKeys.currentUser,
        (current) =>
          current
            ? { ...current, introductionServicesViewed: true }
            : current,
      );
    },
  });

  const { mutate: skipIntroduction, isPending: isSkipping } = useMutation({
    mutationFn: skipIntroductionChecklist,
    onSuccess: () => {
      queryClient.setQueryData<CurrentUser | null>(
        queryKeys.currentUser,
        (current) =>
          current
            ? {
                ...current,
                introductionServicesViewed: true,
                introductionSettingsCompleted: true,
                introductionSkipped: true,
                introductionExpired: true,
              }
            : current,
      );
    },
  });

  useEffect(() => {
    if (
      pathname.startsWith("/services") &&
      user?.accountStatus === "active" &&
      user.onboardingCompleted &&
      !user.introductionServicesViewed &&
      !user.introductionSkipped &&
      !user.introductionExpired &&
      !isMarkingServices
    ) {
      markServicesViewed();
    }
  }, [isMarkingServices, markServicesViewed, pathname, user]);

  useEffect(() => {
    if (
      user?.accountStatus !== "active" ||
      !user.onboardingCompleted ||
      user.introductionSkipped ||
      isSkipping
    ) {
      return;
    }

    const remaining =
      new Date(user.createdAt).getTime() +
      INTRODUCTION_LIFETIME_MS -
      Date.now();

    if (remaining <= 0) {
      skipIntroduction();
      return;
    }

    const timeout = window.setTimeout(() => {
      skipIntroduction();
    }, remaining);

    return () => window.clearTimeout(timeout);
  }, [isSkipping, skipIntroduction, user]);

  return null;
}
